import { AlertTriangle, CheckCircle2, Circle, Loader2, RotateCcw, ScanLine } from "lucide-react";

const DXF_ERR = "We couldn't read this DXF. Please export it to PDF and try again.";
const LABEL = { pending: "Waiting", running: "Reading", done: "Done", failed: "Couldn't read" };

function StatusIcon({ s }) {
  if (s === "done") return <CheckCircle2 className="h-4 w-4 text-emerald-400" />;
  if (s === "failed") return <AlertTriangle className="h-4 w-4 text-red-400" />;
  if (s === "running") return <Loader2 className="h-4 w-4 animate-spin text-amber-400" />;
  return <Circle className="h-4 w-4 text-slate-600" />;
}

export function BatchFiles({ batch, onRetry }) {
  return (
    <ul data-testid="batch-files" className="divide-y divide-slate-800 overflow-hidden rounded-xl border border-slate-800">
      {batch.files.map((f, i) => {
        const status = f.status === "running" && f.phase === "waiting" ? "pending" : f.status;
        return (
          <li key={f.extraction_id} data-testid={`batch-file-${i}`} className="flex items-center gap-3 px-4 py-3">
            <StatusIcon s={status} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-100">{f.filename}</p>
              <p data-testid={`batch-file-status-${i}`} className={`text-xs ${status === "failed" ? "text-red-400" : "text-slate-500"}`}>
                {LABEL[status]}{status === "done" ? ` · ${f.items_found} item${f.items_found === 1 ? "" : "s"}${f.has_schedule === false ? " · plan (nominal)" : f.has_schedule ? " · schedule" : ""}` : ""}
                {status === "running" && f.phase === "closer_look" ? " · taking a closer look" : ""}
                {status === "failed" && f.error ? ` · ${f.error}` : ""}
              </p>
            </div>
            {status === "failed" && f.error !== DXF_ERR && onRetry && (
              <button data-testid={`batch-file-retry-${i}`} onClick={() => onRetry(f.extraction_id)}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-200 transition-colors hover:border-amber-500/60 hover:text-amber-200">
                <RotateCcw className="h-3.5 w-3.5" /> Try again
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function BatchProgress({ batch, onRetry }) {
  const files = batch?.files || [];
  const doneCount = files.filter((f) => f.status === "done" || f.status === "failed").length;
  const current = files.find((f) => f.status === "running" && f.phase !== "waiting") || files.find((f) => f.status !== "done" && f.status !== "failed");
  const idx = current ? files.indexOf(current) + 1 : files.length;
  return (
    <div data-testid="extraction-progress-indicator" className="relative overflow-hidden rounded-2xl border border-slate-800 bg-[#F2F2F7]/80 p-8">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-amber-500/10 to-transparent scan-line" />
      <div className="relative space-y-6">
        <div className="flex items-center gap-4">
          <div className="relative grid h-14 w-14 shrink-0 place-items-center">
            <span className="absolute inset-0 animate-ping rounded-full bg-amber-500/15" />
            <ScanLine className="h-7 w-7 text-amber-400" />
          </div>
          <div>
            <h2 data-testid="extraction-status-text" className="font-display text-2xl font-bold text-slate-50">
              {files.length ? `Reading ${idx} of ${files.length}: ${current?.filename || ""}…` : "Uploading…"}
            </h2>
            <p data-testid="extraction-status-subtext" className="mt-1 text-sm text-slate-400">
              {doneCount} of {files.length} finished · files are read in parallel, usually 20–90 seconds in total.
            </p>
          </div>
        </div>
        {files.length > 0 && <BatchFiles batch={batch} onRetry={onRetry} />}
      </div>
    </div>
  );
}
